/**
 * Combinações de títulos ERP para conciliação multi-conta.
 * Só combina títulos do mesmo fornecedor / cliente / usuário.
 */

import {
  AMOUNT_TOLERANCE,
  calculateErpTitleRemainingBalance,
} from './erpTitlePayment';

/**
 * Chave da parte (fornecedor, cliente ou usuário).
 * null = sem vínculo identificável (não entra em combinação).
 */
export function getTitlePartyKey(title = {}) {
  if (title.supplier_id != null && title.supplier_id !== '') {
    return `s:${title.supplier_id}`;
  }
  if (title.customer_id != null && title.customer_id !== '') {
    return `c:${title.customer_id}`;
  }
  if (title.user_id != null && title.user_id !== '') {
    return `u:${title.user_id}`;
  }

  const name = String(
    title.entity ||
      title.supplier_name ||
      title.customer_name ||
      title.user_name ||
      ''
  )
    .trim()
    .toLowerCase();

  if (!name || name === 'n/a' || name === 'na') {
    return null;
  }
  return `n:${name}`;
}

export function getTitlePartyLabel(title = {}) {
  return (
    title.entity ||
    title.supplier_name ||
    title.customer_name ||
    title.user_name ||
    'Parte não identificada'
  );
}

/**
 * Encontra combinações de 2–N títulos cuja soma ≈ valor da transação,
 * apenas dentro do mesmo partyKey.
 *
 * @returns {Array<{ titles: object[], sum: number, partyKey: string, partyLabel: string, exact: boolean }>}
 */
export function findErpTitleCombinations(titles, transactionValue, options = {}) {
  const {
    maxSize = 4,
    maxResults = 5,
    getBalance = calculateErpTitleRemainingBalance,
  } = options;

  const target = Math.abs(parseFloat(transactionValue) || 0);
  if (!Array.isArray(titles) || titles.length < 2 || target <= AMOUNT_TOLERANCE) {
    return [];
  }

  const eligible = titles.filter((t) => getBalance(t) > AMOUNT_TOLERANCE);
  if (eligible.length < 2) return [];

  /** @type {Map<string, object[]>} */
  const groups = new Map();
  for (const title of eligible) {
    const key = getTitlePartyKey(title);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(title);
  }

  const combinations = [];

  for (const [partyKey, groupTitles] of groups.entries()) {
    if (groupTitles.length < 2) continue;

    const sizeCap = Math.min(maxSize, groupTitles.length);
    const sorted = [...groupTitles].sort((a, b) => getBalance(b) - getBalance(a));

    const walk = (startIndex, current, currentSum) => {
      if (
        Math.abs(currentSum - target) < AMOUNT_TOLERANCE &&
        current.length >= 2
      ) {
        combinations.push({
          titles: [...current],
          sum: currentSum,
          partyKey,
          partyLabel: getTitlePartyLabel(current[0]),
          exact: true,
        });
      }

      if (current.length >= sizeCap || currentSum >= target + AMOUNT_TOLERANCE) {
        return;
      }

      for (let i = startIndex; i < sorted.length; i++) {
        const title = sorted[i];
        const next = currentSum + getBalance(title);
        if (next <= target + AMOUNT_TOLERANCE) {
          current.push(title);
          walk(i + 1, current, next);
          current.pop();
        }
      }
    };

    walk(0, [], 0);
  }

  return combinations
    .sort((a, b) => {
      const diffA = Math.abs(a.sum - target);
      const diffB = Math.abs(b.sum - target);
      if (diffA !== diffB) return diffA - diffB;
      return a.titles.length - b.titles.length;
    })
    .slice(0, maxResults);
}
